import { useMemo, useState } from 'react';
import { formatCompact } from './TrendChart';
import { arcPath, formatPercent, rampStep } from './viz';

export interface SunburstPath {
  /** Ordered pages, e.g. `['/', '/contact', '/aboutus']`. */
  steps: string[];
  value: number;
}

interface SunburstProps {
  paths: SunburstPath[];
  /** Rings drawn. Past 4 the outer arcs are thinner than the labels that would name them. */
  maxDepth?: number;
  size?: number;
  centerLabel?: string;
  valueLabel?: string;

  /**
   * What one ring IS, singular — "Page" for a site journey, "Feature" for a product path.
   *
   * The legend used to say "Page 1..4" unconditionally, which was correct for the only caller that
   * existed when it was written and wrong the moment a second one arrived: phase 3's rings are
   * features, and the chart confidently labelled them pages. Found by looking at a screenshot; no
   * type or palette check can see a caption that is merely untrue.
   */
  stepNoun?: string;
}

interface Node {
  key: string;
  label: string;
  depth: number;
  value: number;
  trail: string[];
  children: Map<string, Node>;
}

/**
 * Journey branching, as concentric rings.
 *
 * <b>A journey is a hierarchy, so the chart is one.</b> The inner ring is where visits start; each
 * ring outward is the next page. Where a ring splits, visitors split — and the width of each split
 * is the share that took it. A table of the top journeys can rank sequences but cannot show that
 * 70% of everyone who lands on <code>/</code> goes to the same second page.
 *
 * <b>Colour encodes DEPTH, not identity.</b> Depth is ordered, so the sequential ramp is legal;
 * every ring is one step darker than the one inside it. Siblings within a ring share a step and
 * are told apart by the 2px surface gap between them and by hover — which is why this ships a
 * table twin rather than treating the picture as sufficient.
 *
 * Not a replacement for the flow diagram. Flow shows direction between two named pages; this shows
 * where a visit divides. They answer different questions and both are on the page.
 */
export function Sunburst({
  paths, maxDepth = 4, size = 260, centerLabel = 'visits', valueLabel = 'Sessions',
  stepNoun = 'Page',
}: SunburstProps) {
  const [hoverKey, setHoverKey] = useState<string | null>(null);

  const root = useMemo(() => buildTree(paths, maxDepth), [paths, maxDepth]);

  if (root.value <= 0) {
    return <div className="empty-state t-body">No journeys recorded for this period yet.</div>;
  }

  const cx = size / 2;
  const cy = size / 2;
  const rHole = size * 0.17;
  const ringWidth = (size / 2 - 6 - rHole) / maxDepth;

  const arcs: {
    key: string; label: string; depth: number; value: number; trail: string[];
    from: number; to: number; path: string;
  }[] = [];

  // Angular gap shrinks with depth: the same 2px reads as a hairline on the inner ring and as a
  // wedge on the outer one, so it is computed at each ring's own radius.
  const walk = (node: Node, from: number, extent: number) => {
    let cursor = from;
    const children = [...node.children.values()].sort((a, b) => b.value - a.value);

    for (const child of children) {
      const sweep = (child.value / node.value) * extent;
      const rInner = rHole + (child.depth - 1) * ringWidth;
      const rOuter = rInner + ringWidth;
      const gap = Math.min((2 / (2 * Math.PI * rOuter)) * 360 * 1.5, sweep / 3);

      arcs.push({
        key: child.key,
        label: child.label,
        depth: child.depth,
        value: child.value,
        trail: child.trail,
        from: cursor,
        to: cursor + sweep,
        path: arcPath(cx, cy, rInner + 1, rOuter, cursor, cursor + sweep - gap),
      });

      if (child.depth < maxDepth) walk(child, cursor, sweep);
      cursor += sweep;
    }
  };
  walk(root, 0, 360);

  const active = arcs.find((a) => a.key === hoverKey) ?? null;

  return (
    <div className="sunburst">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img"
           aria-label={`Journey branching over ${root.value} sessions, ${maxDepth} ${stepNoun.toLowerCase()}s deep.`}>
        {arcs.map((a) => (
          <path
            key={a.key}
            d={a.path}
            fill={rampStep(a.depth - 1, maxDepth)}
            className="sunburst-seg"
            // Ancestors stay lit with the hovered arc — the branch being read is the whole trail,
            // not one ring of it.
            opacity={hoverKey === null || active === null || a.key === hoverKey
              ? 1
              : (hoverKey.startsWith(a.key) ? 0.85 : 0.25)}
            onMouseEnter={() => setHoverKey(a.key)}
            onMouseLeave={() => setHoverKey(null)}
          >
            <title>{`${a.trail.join(' › ')} — ${formatCompact(a.value)} (${formatPercent(a.value / root.value)})`}</title>
          </path>
        ))}

        <text x={cx} y={cy - 2} textAnchor="middle" className="sunburst-hero tabular">
          {active ? formatPercent(active.value / root.value, 0) : formatCompact(root.value)}
        </text>
        <text x={cx} y={cy + 13} textAnchor="middle" className="sunburst-sub">
          {active ? `step ${active.depth}` : centerLabel}
        </text>
      </svg>

      <div className="sunburst-side">
        <div className="sunburst-trail t-label">
          {active
            ? active.trail.map((step, i) => (
                <span key={`${step}-${i}`}>
                  {i > 0 && <span className="subtle"> › </span>}
                  <span>{step}</span>
                </span>
              ))
            : <span className="subtle">Hover a ring to trace a branch.</span>}
        </div>

        <ul className="sunburst-legend t-caption">
          {Array.from({ length: maxDepth }, (_, i) => (
            <li key={i}>
              <i style={{ background: rampStep(i, maxDepth) }} />
              {stepNoun} {i + 1}
            </li>
          ))}
        </ul>
      </div>

      <details className="viz-table">
        <summary className="t-label">Table view</summary>
        <table className="table">
          <thead>
            <tr><th>Branch</th><th className="num">Step</th><th className="num">{valueLabel}</th><th className="num">Share</th></tr>
          </thead>
          <tbody>
            {[...arcs]
              .sort((a, b) => b.value - a.value || a.depth - b.depth)
              .slice(0, 40)
              .map((a) => (
                <tr key={a.key}>
                  <td>{a.trail.join(' › ')}</td>
                  <td className="num">{a.depth}</td>
                  <td className="num">{a.value.toLocaleString('en-US')}</td>
                  <td className="num">{formatPercent(a.value / root.value)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </details>
    </div>
  );
}

/**
 * Folds the flat journey list into a prefix tree.
 *
 * A node's value is the sessions that passed THROUGH it, so a parent always holds at least the sum
 * of its children — the leftover is the visits that ended there, and that is exactly what the gap
 * between one ring and the next represents.
 */
function buildTree(paths: SunburstPath[], maxDepth: number): Node {
  const root: Node = { key: '', label: '', depth: 0, value: 0, trail: [], children: new Map() };

  for (const path of paths) {
    if (!Number.isFinite(path.value) || path.value <= 0 || path.steps.length === 0) continue;

    root.value += path.value;
    let node = root;

    for (const step of path.steps.slice(0, maxDepth)) {
      const key = `${node.key}\u0000${step}`;
      let child = node.children.get(key);
      if (!child) {
        child = {
          key,
          label: step,
          depth: node.depth + 1,
          value: 0,
          trail: [...node.trail, step],
          children: new Map(),
        };
        node.children.set(key, child);
      }
      child.value += path.value;
      node = child;
    }
  }

  return root;
}
